/**
 * Study Action Dialogs — container component that renders the appropriate
 * dialog based on the studyActionStore state.
 *
 * Mounted once in AppShell — all study action dialogs render from here.
 */
import { useStudyActionStore } from '../../stores/studyActionStore';
import { useRefreshStudies } from './useStudies';
import { AssignStudyDialog } from './AssignStudyDialog';
import { StudyNotesDialog } from './StudyNotesDialog';
import { StudyLogDialog } from './StudyLogDialog';
import { EditStudyDialog } from './EditStudyDialog';
import { SendStudyDialog } from './SendStudyDialog';
import { CompleteStudyDialog } from './CompleteStudyDialog';
import { ReportEditorDialog } from '../reports/ReportEditorDialog';
import { EditPatientDialog } from './EditPatientDialog';
import { PeerReviewDialog } from './PeerReviewDialog';
import { StudyLocationDialog } from './StudyLocationDialog';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { ShareStudyDialog } from './ShareStudyDialog';
import { MissedSlaDialog } from './MissedSlaDialog';
import { ArrivePatientDialog } from './ArrivePatientDialog';
import { DiscordanceDialog } from './DiscordanceDialog';
import { CallContactsDialog } from './CallContactsDialog';
import { StudyAttachmentsDialog } from './StudyAttachmentsDialog';
import { AutoAddOrderDialog } from './AutoAddOrderDialog';
import { OrderEditorDialog } from './OrderEditorDialog';
import { ScheduleOrderDialog } from './ScheduleOrderDialog';
import { RescheduleOrderDialog } from './RescheduleOrderDialog';
import { AuthOrderDialog } from './AuthOrderDialog';
import { PaymentDialog } from './PaymentDialog';
import { PatientOrderHistoryDialog } from './PatientOrderHistoryDialog';
import { UploadRequestDialog } from './UploadRequestDialog';
import { StorageOptionsDialog } from './StorageOptionsDialog';
import { PatientDiscoveryDialog } from './PatientDiscoveryDialog';
import { MarkImportantDialog } from './MarkImportantDialog';
import { MarkFollowUpDialog } from './MarkFollowUpDialog';
import { DicomDumpDialog } from './DicomDumpDialog';

export const StudyActionDialogs = () => {
  const { activeDialog, dialogStudies, closeDialog } = useStudyActionStore();
  const refreshStudies = useRefreshStudies();

  const handleComplete = () => {
    refreshStudies();
  };

  return (
    <>
      <AssignStudyDialog
        open={activeDialog === 'assignStudy'}
        studies={dialogStudies}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <StudyNotesDialog
        open={activeDialog === 'studyNotes'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
      />
      <StudyLogDialog
        open={activeDialog === 'studyLog'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
      />
      <EditStudyDialog
        open={activeDialog === 'editStudy'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <SendStudyDialog
        open={activeDialog === 'sendStudy'}
        studies={dialogStudies}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <ReportEditorDialog
        open={activeDialog === 'createReport'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
        mode="create"
      />
      <ReportEditorDialog
        open={activeDialog === 'editReport'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
        mode="edit"
      />
      <CompleteStudyDialog
        open={activeDialog === 'completeStudy'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <EditPatientDialog
        open={activeDialog === 'editPatient'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <PeerReviewDialog
        open={activeDialog === 'peerReview'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <StudyLocationDialog
        open={activeDialog === 'studyLocation'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
      />
      <ConfirmActionDialog
        open={activeDialog === 'confirmAction'}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <ShareStudyDialog
        open={activeDialog === 'shareStudy'}
        studies={dialogStudies}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <MissedSlaDialog
        open={activeDialog === 'missedSla'}
        studies={dialogStudies}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <ArrivePatientDialog
        open={activeDialog === 'arrivePatient'}
        studies={dialogStudies}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <DiscordanceDialog
        open={activeDialog === 'discordance'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <CallContactsDialog
        open={activeDialog === 'callContacts'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
      />
      <StudyAttachmentsDialog
        open={activeDialog === 'attachments'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <AutoAddOrderDialog
        open={activeDialog === 'autoAddOrder'}
        studies={dialogStudies}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <OrderEditorDialog
        open={activeDialog === 'editOrder'}
        study={dialogStudies[0] ?? null}
        mode="edit"
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <OrderEditorDialog
        open={activeDialog === 'addOrder'}
        study={dialogStudies[0] ?? null}
        mode="add"
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <ScheduleOrderDialog
        open={activeDialog === 'scheduleOrder'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <RescheduleOrderDialog
        open={activeDialog === 'rescheduleOrder'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <AuthOrderDialog
        open={activeDialog === 'authOrder'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <PaymentDialog
        open={activeDialog === 'payment'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <PatientOrderHistoryDialog
        open={activeDialog === 'patientOrderHistory'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
      />
      <UploadRequestDialog
        open={activeDialog === 'uploadRequest'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <StorageOptionsDialog
        open={activeDialog === 'storageOptions'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <PatientDiscoveryDialog
        open={activeDialog === 'patientDiscovery'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
      />
      <MarkImportantDialog
        open={activeDialog === 'markImportant'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <MarkFollowUpDialog
        open={activeDialog === 'markFollowUp'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
        onComplete={handleComplete}
      />
      <DicomDumpDialog
        open={activeDialog === 'dicomDump'}
        study={dialogStudies[0] ?? null}
        onClose={closeDialog}
      />
    </>
  );
};
